import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const VersionHistory = ({ document, onUpdate }) => {
  const { user } = useAuth();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [comparing, setComparing] = useState(null);

  useEffect(() => {
    if (document?.id) {
      loadVersions();
    }
  }, [document?.id]);

  const loadVersions = async () => {
    try {
      setLoading(true);
      setError('');

      const { data, error: fetchError } = await supabase
        ?.from('document_versions')
        ?.select(`
          *,
          user_profiles!document_versions_created_by_fkey(full_name, email)
        `)
        ?.eq('document_id', document?.id)
        ?.order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setVersions(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (versionId) => {
    if (!confirm('Are you sure you want to restore this version?')) return;

    try {
      // Update all versions to not current
      await supabase
        ?.from('document_versions')
        ?.update({ is_current: false })
        ?.eq('document_id', document?.id);

      // Set selected version as current
      const { error: updateError } = await supabase
        ?.from('document_versions')
        ?.update({ is_current: true })
        ?.eq('id', versionId);

      if (updateError) throw updateError;

      await loadVersions();
      onUpdate?.();
    } catch (err) {
      setError(err?.message || 'Failed to restore version');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    return `${(bytes / 1024 / 1024)?.toFixed(2)} MB`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-sm text-muted-foreground">Loading version history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
        {error}
      </div>
    );
  }

  if (versions?.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Icon name="GitBranch" size={48} className="mx-auto mb-3 opacity-20" />
        <p className="text-sm">No version history available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">Version History</h3>
          <p className="text-sm text-muted-foreground">{versions?.length} versions available</p>
        </div>
        <Button variant="outline" size="sm" iconName="Plus" iconPosition="left">
          Create New Version
        </Button>
      </div>

      <div className="space-y-3">
        {versions?.map((version, idx) => (
          <div
            key={version?.id}
            className={`
              p-4 rounded-lg border transition-all duration-200
              ${version?.is_current
                ? 'bg-primary/5 border-primary/20' :'bg-card border-border hover:border-primary/20'
              }
            `}
          >
            <div className="flex items-start gap-4">
              <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg flex-shrink-0">
                <Icon name="GitCommit" size={20} color="var(--color-primary)" />
              </div>

              <div className="flex-1">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-base font-semibold">{version?.version_number}</h4>
                      {version?.is_current && (
                        <span className="px-2 py-0.5 text-xs rounded bg-primary/10 text-primary font-medium">
                          Current
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {version?.changes_summary || 'No description provided'}
                    </p>
                  </div>
                  {!version?.is_current && (
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="RotateCcw"
                      onClick={() => handleRestore(version?.id)}
                    >
                      Restore
                    </Button>
                  )}
                </div>

                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Icon name="User" size={12} />
                    {version?.user_profiles?.full_name || 'Unknown'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="Calendar" size={12} />
                    {new Date(version?.created_at)?.toLocaleString()}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="HardDrive" size={12} />
                    {formatFileSize(version?.file_size)}
                  </span>
                </div>

                {idx < versions?.length - 1 && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="GitCompare"
                      iconPosition="left"
                      onClick={() => setComparing(comparing === version?.id ? null : version?.id)}
                    >
                      Compare with previous
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {comparing === version?.id && (
              <div className="mt-4 p-4 bg-background rounded-lg border border-border">
                <p className="text-sm text-muted-foreground">
                  Version comparison feature coming soon. This will show side-by-side diff of changes.
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default VersionHistory;